import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSpring, animated } from 'react-spring';
import { useTheme } from '../contexts/ThemeContext';
import { useMessaging } from '../contexts/MessagingContext';
import { useAuth } from '../contexts/AuthContext';
import { useClerk } from '@clerk/clerk-react';
import {
  FaMoon,
  FaSun,
  FaBars,
  FaTimes,
  FaGamepad,
  FaStore,
  FaSearch,
  FaUpload,
  FaHandshake,
  FaHeart,
  FaComments,
  FaUserShield,
  FaSignInAlt,
  FaUserPlus,
  FaSignOutAlt,
  FaUser,
  FaListAlt,
  FaChevronDown,
  FaReceipt,
  FaLayerGroup,
  FaUserFriends,
  FaMapMarkerAlt,
} from 'react-icons/fa';
import Logo from './Logo';
import '../styles/Navbar.css';

const Navbar = () => {
  const { toggleTheme, isDark } = useTheme();
  const { getTotalUnreadCount } = useMessaging();
  const { user, isAdmin, isAuthenticated } = useAuth();
  const { signOut } = useClerk();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);

  const handleLogout = async () => {
    await signOut();
    navigate('/');
    setIsMobileMenuOpen(false);
    setIsUserDropdownOpen(false);
  };

  const handleDropdownKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Escape') {
      setIsUserDropdownOpen(false);
      e.currentTarget.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const firstItem = e.currentTarget.nextElementSibling?.querySelector(
        '.dropdown-item'
      ) as HTMLElement | null;
      firstItem?.focus();
    }
  };

  const handleDropdownItemKeyDown = (
    e: React.KeyboardEvent<HTMLElement>,
    index: number
  ) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextItem = e.currentTarget.parentElement.querySelector(
        `.dropdown-item:nth-child(${index + 2})`
      ) as HTMLElement | null;
      nextItem?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (index === 0) {
        const button = e.currentTarget.parentElement
          .previousElementSibling as HTMLElement | null;
        button?.focus();
        setIsUserDropdownOpen(false);
      } else {
        const prevItem = e.currentTarget.parentElement.querySelector(
          `.dropdown-item:nth-child(${index})`
        ) as HTMLElement | null;
        prevItem?.focus();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      const button = e.currentTarget.parentElement
        .previousElementSibling as HTMLElement | null;
      setIsUserDropdownOpen(false);
      button?.focus();
    }
  };

  // Main navigation items (always visible)
  const mainMenuItems = [
    { path: '/', label: 'Home', icon: FaGamepad },
    { path: '/market', label: 'Market', icon: FaStore },
    { path: '/find-games', label: 'Find Games', icon: FaSearch },
    {
      path: '/libraries/nearby',
      label: 'Nearby Libraries',
      icon: FaMapMarkerAlt,
    },
    { path: '/upload', label: 'Upload Game', icon: FaUpload },
    { path: '/messages', label: 'Messages', icon: FaComments },
  ];

  // User dropdown items (shown when authenticated)
  const userDropdownItems = [
    { path: '/profile', label: 'My Profile', icon: FaUser },
    { path: '/friends', label: 'Friends', icon: FaUserFriends },
    { path: '/my-games', label: 'My Games', icon: FaListAlt },
    { path: '/libraries', label: 'Libraries', icon: FaLayerGroup },
    { path: '/favorites', label: 'Favorites', icon: FaHeart },
    { path: '/borrow', label: 'Borrow Requests', icon: FaHandshake },
    { path: '/payments-history', label: 'Payment History', icon: FaReceipt }, // View all transactions
  ];

  // Add admin to dropdown if user is admin
  if (isAdmin) {
    userDropdownItems.push({
      path: '/admin',
      label: 'Admin Dashboard',
      icon: FaUserShield,
    });
  }

  const navbarAnimation = useSpring({
    from: { y: -100, opacity: 0 },
    to: { y: 0, opacity: 1 },
    config: { tension: 300, friction: 30 },
  });

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <animated.nav style={navbarAnimation} className="navbar">
      <div className="navbar-container">
        <Link
          to="/"
          className="navbar-brand"
          aria-label="Table Top Archive Home"
        >
          <Logo />
        </Link>

        <nav className="navbar-menu" aria-label="Main navigation">
          {mainMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            const unreadCount =
              item.path === '/messages' ? getTotalUnreadCount() : 0;

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`navbar-link ${isActive ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
                aria-current={isActive ? 'page' : undefined}
              >
                <div className="link-icon-wrapper">
                  <Icon className="link-icon" aria-hidden="true" />
                  {unreadCount > 0 && (
                    <span
                      className="unread-badge"
                      aria-label={`${unreadCount} unread messages`}
                    >
                      {unreadCount}
                    </span>
                  )}
                </div>
                <span className="link-text">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="navbar-actions">
          {isAuthenticated ? (
            <div className="user-profile-wrapper">
              <button
                className="user-profile-button"
                onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                onBlur={() =>
                  setTimeout(() => setIsUserDropdownOpen(false), 200)
                }
                onKeyDown={handleDropdownKeyDown}
                aria-expanded={isUserDropdownOpen}
                aria-haspopup="menu"
                aria-label="User menu"
              >
                <FaUser className="user-icon" aria-hidden="true" />
                <span className="user-name">{user?.name || user?.email}</span>
                <FaChevronDown
                  className={`chevron ${isUserDropdownOpen ? 'open' : ''}`}
                  aria-hidden="true"
                />
              </button>

              {isUserDropdownOpen && (
                <div
                  className="user-dropdown"
                  role="menu"
                  aria-label="User options"
                >
                  {userDropdownItems.map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="dropdown-item"
                        onClick={() => setIsUserDropdownOpen(false)}
                        onKeyDown={(e) => handleDropdownItemKeyDown(e, index)}
                        role="menuitem"
                      >
                        <Icon className="dropdown-icon" aria-hidden="true" />
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                  <div className="dropdown-divider" role="separator"></div>
                  <button
                    onClick={handleLogout}
                    onKeyDown={(e) =>
                      handleDropdownItemKeyDown(e, userDropdownItems.length)
                    }
                    className="dropdown-item logout-item"
                    role="menuitem"
                  >
                    <FaSignOutAlt
                      className="dropdown-icon"
                      aria-hidden="true"
                    />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link to="/login" className="auth-button login-btn">
                <FaSignInAlt /> Login
              </Link>
              <Link to="/register" className="auth-button register-btn">
                <FaUserPlus /> Sign Up
              </Link>
            </>
          )}

          <button
            onClick={toggleTheme}
            className="theme-toggle"
            aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
          >
            {isDark ? <FaSun /> : <FaMoon />}
          </button>

          <button
            className="mobile-menu-toggle"
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="mobile-menu">
          {mainMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            const unreadCount =
              item.path === '/messages' ? getTotalUnreadCount() : 0;

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`mobile-menu-link ${isActive ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
                aria-current={isActive ? 'page' : undefined}
              >
                <div className="link-icon-wrapper">
                  <Icon className="link-icon" aria-hidden="true" />
                  {unreadCount > 0 && (
                    <span
                      className="unread-badge"
                      aria-label={`${unreadCount} unread messages`}
                    >
                      {unreadCount}
                    </span>
                  )}
                </div>
                <span>{item.label}</span>
              </Link>
            );
          })}

          {isAuthenticated && (
            <>
              <div className="mobile-menu-divider"></div>
              <div className="mobile-user-section">
                <div className="mobile-user-info">
                  <FaUser /> {user?.name || user?.email}
                </div>
                {userDropdownItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="mobile-menu-link"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Icon className="link-icon" />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
                <button
                  onClick={handleLogout}
                  className="mobile-menu-link logout-link"
                >
                  <FaSignOutAlt className="link-icon" />
                  <span>Logout</span>
                </button>
              </div>
            </>
          )}

          {!isAuthenticated && (
            <div className="mobile-auth-section">
              <Link
                to="/login"
                className="mobile-auth-button login-btn"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <FaSignInAlt /> Login
              </Link>
              <Link
                to="/register"
                className="mobile-auth-button register-btn"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <FaUserPlus /> Sign Up
              </Link>
            </div>
          )}
        </div>
      )}
    </animated.nav>
  );
};

export default Navbar;
